"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableValidationTools = void 0;
const __1 = require("../");
const validation_check_result_1 = require("./validation-check-result");
class TableValidationTools {
    static checkValueSIParseability(entries, propertyName, characteristicType, options) {
        const { ignoreEmpty = false } = options ?? {};
        const failures = [];
        const siDefinition = __1.BoundaryCharacteristicSIDefinitions[characteristicType];
        entries.forEach((entry, i) => {
            const value = entry[propertyName];
            if (ignoreEmpty && (value === null || value === undefined || value === '')) {
                return;
            }
            if (typeof value === 'string' || typeof value === 'number') {
                try {
                    const parsed = __1.Utils.parseNumericalSIValue(value, siDefinition);
                    if (parsed === undefined) {
                        failures.push({
                            index: i,
                            message: `Failed to parse value '${value}' of property '${String(propertyName)}' - returned undefined.`,
                        });
                    }
                }
                catch (error) {
                    const errorMessage = error instanceof Error ? error.message : String(error);
                    failures.push({
                        index: i,
                        message: `Failed to parse value '${value}' of property '${String(propertyName)}' using parseNumericalSIValue.`,
                        details: errorMessage,
                    });
                }
            }
            else if (value !== null && value !== undefined) {
                failures.push({
                    index: i,
                    message: `Property '${String(propertyName)}' has invalid type '${typeof value}' (expected string or number).`,
                });
            }
        });
        return failures;
    }
    static checkValueBelongsToSet(entries, propertyName, allowedValues, options) {
        const { ignoreEmpty = false } = options ?? {};
        const failures = [];
        entries.forEach((entry, i) => {
            const value = entry[propertyName];
            if (ignoreEmpty && (value === null || value === undefined)) {
                return;
            }
            if (!allowedValues.has(value)) {
                const allowedValuesStr = Array.from(allowedValues)
                    .map((v) => JSON.stringify(v))
                    .join(', ');
                failures.push({
                    index: i,
                    message: `Property '${String(propertyName)}' has value ${JSON.stringify(value)} which is not in the allowed set.`,
                    details: `Set: [${allowedValuesStr}]`,
                });
            }
        });
        return failures;
    }
    static checkValueIsValidSignal(entries, propertyName, signals) {
        const isStringArray = (arr) => {
            return arr.length === 0 || typeof arr[0] === 'string';
        };
        const signalNames = isStringArray(signals) ? signals : signals.map((s) => s.name);
        const signalNamesSet = new Set(signalNames);
        const failures = this.checkValueBelongsToSet(entries, propertyName, signalNamesSet, { ignoreEmpty: false });
        return failures.map((failure) => {
            const entry = entries[failure.index];
            const value = entry[propertyName];
            return {
                ...failure,
                message: `Property '${String(propertyName)}' has value '${value}' which is not an allowed signal`,
            };
        });
    }
    static checkValueIsNotEmpty(entries, propertyName) {
        const failures = [];
        entries.forEach((entry, i) => {
            const value = entry[propertyName];
            if (value === null || value === undefined || value === '') {
                failures.push({
                    index: i,
                    message: `Property '${String(propertyName)}' is empty.`,
                });
            }
        });
        return failures;
    }
    static checkListPropertyHasMinimumEntryCount(entries, propertyName, minEntries = 1) {
        const failures = [];
        entries.forEach((entry, i) => {
            const value = entry[propertyName];
            if (value === null || value === undefined) {
                failures.push({
                    index: i,
                    message: `Property '${String(propertyName)}' is null or undefined but requires at least ${minEntries} entries.`,
                });
                return;
            }
            if (!Array.isArray(value)) {
                failures.push({
                    index: i,
                    message: `Property '${String(propertyName)}' has invalid type '${typeof value}' (expected array).`,
                });
                return;
            }
            if (value.length < minEntries) {
                failures.push({
                    index: i,
                    message: `Property '${String(propertyName)}' has ${value.length} entries but requires at least ${minEntries}.`,
                });
            }
        });
        return failures;
    }
    static checkPropertyValuesDiffer(entries, propertyName1, propertyName2) {
        const failures = [];
        entries.forEach((entry, i) => {
            const value1 = entry[propertyName1];
            const value2 = entry[propertyName2];
            if (value1 === value2) {
                failures.push({
                    index: i,
                    message: `Properties '${String(propertyName1)}' and '${String(propertyName2)}' have the same value: ${JSON.stringify(value1)}.`,
                });
            }
        });
        return failures;
    }
    static checkValueIsNotInSet(entries, propertyName, forbiddenValues, options) {
        const { ignoreEmpty = false } = options ?? {};
        const failures = [];
        entries.forEach((entry, i) => {
            const value = entry[propertyName];
            if (ignoreEmpty && (value === null || value === undefined)) {
                return;
            }
            if (forbiddenValues.has(value)) {
                const forbiddenValuesStr = Array.from(forbiddenValues)
                    .map((v) => JSON.stringify(v))
                    .join(', ');
                failures.push({
                    index: i,
                    message: `Property '${String(propertyName)}' has value ${JSON.stringify(value)} which is in the forbidden set.`,
                    details: `Forbidden set: [${forbiddenValuesStr}]`,
                });
            }
        });
        return failures;
    }
    static checkForDuplicateEntries(entries) {
        const failures = [];
        const hashToFirstIndex = new Map();
        entries.forEach((entry, i) => {
            const sortedEntry = __1.Utils.sortObjectKeysRecursively(entry);
            const hash = JSON.stringify(sortedEntry);
            if (hashToFirstIndex.has(hash)) {
                failures.push({
                    index: i,
                    message: 'Duplicate entry found.',
                    details: `Duplicate entry: ${hash}`,
                });
            }
            else {
                hashToFirstIndex.set(hash, i);
            }
        });
        return failures;
    }
    static applyValidationCheck(entries, validationCallable, validationErrorType, options) {
        const { dropFailingEntries = false, logger } = options;
        let failures;
        try {
            failures = validationCallable(entries);
        }
        catch (error) {
            logger.error('ApplyValidationCheck_UnexpectedError', {
                validationErrorType,
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
            });
            return {
                validatedEntries: entries,
                validationResults: [
                    {
                        type: validationErrorType,
                        result: validation_check_result_1.ValidationCheckResultStatus.fail,
                        message: `Validation check encountered an unexpected error: ${error instanceof Error ? error.message : String(error)}`,
                    },
                ],
            };
        }
        if (failures.length === 0) {
            return {
                validatedEntries: entries,
                validationResults: [
                    {
                        type: validationErrorType,
                        result: validation_check_result_1.ValidationCheckResultStatus.pass,
                        message: 'All entries passed validation',
                    },
                ],
            };
        }
        const validationResults = [];
        const failedIndices = new Set();
        for (const failure of failures) {
            logger.warn('ApplyValidationCheck_ValidationFailure', {
                validationErrorType,
                index: failure.index,
                message: failure.message,
                details: failure.details,
            });
            if (dropFailingEntries) {
                logger.warn('ApplyValidationCheck_DroppedEntry', {
                    validationErrorType,
                    index: failure.index,
                    entry: JSON.stringify(entries[failure.index]),
                });
                failedIndices.add(failure.index);
            }
            validationResults.push({
                type: validationErrorType,
                result: validation_check_result_1.ValidationCheckResultStatus.fail,
                message: failure.message,
            });
        }
        const validatedEntries = dropFailingEntries ? entries.filter((_, index) => !failedIndices.has(index)) : entries;
        return {
            validatedEntries,
            validationResults,
        };
    }
}
exports.TableValidationTools = TableValidationTools;
//# sourceMappingURL=table-validation-tools.js.map